/**
 * One psalm, in full (150 routes).
 *
 * ## Why this page may carry the whole text when the hymn page may not
 *
 * `HymnPage` renders metadata and deliberately NO lyrics: the Hymnal 1982's texts
 * are Church Publishing's copyright. The psalter is the opposite case — the 1979
 * BCP, its Psalter included, is public domain in the United States, so the text
 * itself is the page. That asymmetry is the single most useful fact about this
 * corpus and it is why the psalter was worth building at all.
 *
 * What the BCP does NOT carry is Anglican chant pointing. The asterisk marks the
 * mid-verse division for antiphonal recitation — where one side stops and the other
 * answers — and says nothing about which syllables fall on the reciting note. Every
 * published pointing of this translation is under copyright. So this page prints the
 * asterisk and claims nothing more; the planned per-org pointing overlay is where
 * pointing will live. See `docs/pm/active/canticle-liturgical-resource-registry.md`.
 */
import { Link, useParams } from 'react-router-dom'
import { Breadcrumbs } from '@gjl/studio-chrome'

import { psalmByNumber, BCP_1979_PSALTER, psalter } from '@/data/psalter'
import { appointmentsByPsalm, hasAppointments, rclAttribution } from '@/data/appointments'
import { psalmText, ASTERISK } from '@/lib/psalter'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'

const base = '/hymnal/psalter'

export default function PsalmPage() {
  const { number } = useParams<{ number: string }>()
  // Route param is `psalm-23`; the model keys on the integer.
  const n = Number((number ?? '').replace(/^psalm-/, ''))
  const psalm = Number.isFinite(n) ? psalmByNumber.get(n) : undefined
  if (!psalm)
    return (
      <NotFound
        title="No such psalm"
        detail="The Psalter runs from Psalm 1 to Psalm 150."
        backTo={base}
        backLabel="Browse the Psalter"
      />
    )

  const prev = psalmByNumber.get(psalm.number - 1)
  const next = psalmByNumber.get(psalm.number + 1)
  const appointed = appointmentsByPsalm.get(psalm.number) ?? []

  const description = psalm.latinTitle
    ? `${psalm.title} (${psalm.latinTitle}) in full, from the Psalter of the 1979 Book of Common Prayer — ${psalm.verseCount} verses.`
    : `${psalm.title} in full, from the Psalter of the 1979 Book of Common Prayer — ${psalm.verseCount} verses.`

  return (
    <article className="mx-auto max-w-2xl px-5 py-10">
      <PageHead
        title={psalm.latinTitle ? `${psalm.title} — ${psalm.latinTitle}` : psalm.title}
        description={description}
        path={`${base}/${psalm.slug}`}
        suffix="The Psalter (BCP 1979)"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          name: psalm.title,
          alternateName: psalm.latinTitle || undefined,
          isPartOf: { '@type': 'Book', name: BCP_1979_PSALTER.translation },
          inLanguage: 'en',
          isAccessibleForFree: true,
          license: 'https://en.wikipedia.org/wiki/Public_domain',
        }}
      />

      <Breadcrumbs
        className="mb-6"
        items={[
          { name: 'The Psalter', url: base },
          { name: psalm.title, url: `${base}/${psalm.slug}` },
        ]}
      />

      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">{psalm.title}</h1>
        {/* 8 psalms have no Latin incipit in the dataset — a scrape gap, not a gap
            in the book. Rendering conditionally is what keeps the heading from
            showing an empty line rather than inventing one. */}
        {psalm.latinTitle && <p className="mt-1 text-lg italic text-muted-foreground">{psalm.latinTitle}</p>}
        <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">
          {BCP_1979_PSALTER.translation} · {psalm.verseCount} verses
        </p>
      </header>

      {/* The verse body. The second half is indented, which is how the BCP sets it
          and how a singer finds the handover at a glance. */}
      <ol className="space-y-3">
        {psalm.verses.map((v) => (
          <li key={v.n} className="grid grid-cols-[2.25rem_1fr] gap-x-3">
            <span className="pt-1 text-right font-mono text-xs text-muted-foreground">{v.n}</span>
            <p className="text-[0.975rem] leading-relaxed text-foreground">
              {psalmText(v.halves[0])}
              <span className="text-muted-foreground">{ASTERISK}</span>
              <br />
              <span className="pl-5 inline-block">{psalmText(v.halves[1])}</span>
            </p>
          </li>
        ))}
      </ol>

      {/* Dark until CCT permission exists — see src/data/appointments.ts. The block
          renders nothing today, and renders WITH the required notice the moment the
          gate in that one file opens. */}
      {hasAppointments && appointed.length > 0 && (
        <section className="mt-10 border-t border-border pt-6">
          <h2 className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Appointed for</h2>
          <ul className="space-y-1">
            {appointed.map((a) => (
              <li key={`${a.cycle}-${a.dayId}`} className="text-sm">
                <Link
                  to={`/hymnal/propers/${a.dayId}`}
                  className="underline decoration-border underline-offset-4 hover:decoration-foreground"
                >
                  {a.dayTitle}
                </Link>
                <span className="text-muted-foreground">
                  {' '}
                  — Year {a.cycle}, {a.citation}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">{rclAttribution}</p>
        </section>
      )}

      <section className="mt-10 border-t border-border pt-6">
        <a
          href={`${base}/pdf/${psalm.slug}.pdf`}
          className="inline-block text-sm underline decoration-border underline-offset-4 hover:decoration-foreground"
        >
          Download {psalm.title} as a PDF
        </a>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{BCP_1979_PSALTER.licence}</p>
      </section>

      <nav className="mt-8 flex justify-between text-sm">
        {prev ? (
          <Link to={`${base}/${prev.slug}`} className="text-muted-foreground hover:text-foreground">
            ← {prev.title}
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`${base}/${next.slug}`} className="text-muted-foreground hover:text-foreground">
            {next.title} →
          </Link>
        ) : (
          <span />
        )}
      </nav>

      <p className="mt-8 text-sm">
        <Link to={base} className="text-muted-foreground hover:text-foreground">
          All {psalter.psalms.length} psalms
        </Link>
      </p>
    </article>
  )
}
